import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Card,
  CardHeader,
  CircularProgress,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import MoodIcon from "@material-ui/icons/Mood";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ReplyIcon from "@material-ui/icons/Reply";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SearchIcon from "@material-ui/icons/Search";
import clsx from "clsx";
import { format, isSameDay, parseISO } from "date-fns";
import { toast } from "react-toastify";

import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import { copyTextToClipboard } from "../../helpers/copyToClipboard";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import ChatSearchDrawer from "./ChatSearchDrawer";
import MediaGalleryViewer from "../../components/MediaGalleryViewer";
import { colorFromName, getInitials } from "./chatColors";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

const useStyles = makeStyles((theme) => ({
  // Linha: coluna da conversa + painel lateral de pesquisa.
  mainContainer: {
    display: "flex",
    flexDirection: "row",
    position: "relative",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
  },
  chatColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
  },
  // Mesmo cabecalho da conversa de ticket (components/TicketHeader).
  chatHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.tabHeaderBackground,
    flex: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  headerActions: {
    paddingRight: theme.spacing(1),
    flex: "none",
  },
  headerAvatar: {
    color: "#fff",
    fontWeight: 500,
  },
  messageList: {
    backgroundImage:
      theme.mode === "light"
        ? `url(${whatsBackground})`
        : `url(${whatsBackgroundDark})`,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "20px 20px 20px 20px",
    ...theme.scrollbarStyles,
  },
  historyLoading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },
  // Bolhas iguais as do atendimento: propositalmente claras nos dois temas, com
  // o texto fixo em #303030 para continuar legivel no modo escuro.
  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    "&:hover #chatMessageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    // Estende a area sensivel ao hover para fora da bolha, para o cursor nao
    // "perder" a mensagem ao mirar na setinha.
    "&::after": {
      content: '""',
      position: "absolute",
      top: -8,
      bottom: -8,
      right: -44,
      width: 44,
    },
  },
  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    "&:hover #chatMessageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -8,
      bottom: -8,
      left: -44,
      width: 44,
    },
  },
  // Espaco extra entre blocos de autores diferentes, para o agrupamento ficar
  // visivel sem precisar repetir o nome em toda mensagem.
  messageGroupStart: {
    marginTop: 10,
  },
  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    padding: 2,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },
  // Em telas sem hover (toque) a setinha fica sempre visivel: long-press nao e
  // confiavel — no iOS ele abre o menu nativo de selecao de texto.
  messageActionsButtonAlwaysVisible: {
    display: "flex !important",
    position: "absolute",
    top: 0,
    right: 0,
  },
  // Destaque temporario da mensagem alvo de um salto (pesquisa ou citacao).
  messageJumpHighlight: {
    boxShadow: `0 0 0 3px ${theme.palette.warning.main}, 0 0 0 6px rgba(255, 193, 7, 0.28), 0 1px 1px #b3b3b3`,
    backgroundColor: "rgba(255, 235, 59, 0.22)",
    transition: "background-color 220ms ease, box-shadow 220ms ease",
    animation: "$chatMessageJumpPulse 1.1s ease-in-out 3",
  },
  "@keyframes chatMessageJumpPulse": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.015)" },
    "100%": { transform: "scale(1)" },
  },
  // Bloco da mensagem citada, dentro da bolha. Cores fixas de proposito: as
  // proprias bolhas sao claras nos dois temas.
  quotedContainer: {
    margin: "-2px -6px 6px -1px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: 6,
    display: "flex",
    position: "relative",
    cursor: "pointer",
  },
  quotedContainerRight: {
    backgroundColor: "#cfe9ba",
  },
  quotedSideColor: {
    flex: "none",
    width: 4,
    backgroundColor: "#6bcbef",
  },
  quotedSideColorMine: {
    backgroundColor: "#35cd96",
  },
  quotedBody: {
    padding: "6px 8px",
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#303030",
  },
  quotedSender: {
    display: "block",
    fontSize: 12.5,
    fontWeight: 500,
  },
  quotedText: {
    fontSize: 13,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    maxHeight: 54,
    wordBreak: "break-word",
  },
  quotedThumb: {
    width: 40,
    height: 40,
    borderRadius: 4,
    objectFit: "cover",
    flex: "none",
  },
  // Banner de resposta acima do compositor. Diferente do bloco citado, este PRECISA
  // ser ciente do tema: ele fica sobre newmessagebox (#eee no claro, #333 no escuro).
  replyBannerWrapper: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 12px 0 12px",
    backgroundColor: theme.palette.newmessagebox,
  },
  replyBannerContainer: {
    flex: 1,
    marginRight: 5,
    overflow: "hidden",
    backgroundColor:
      theme.mode === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
    borderRadius: 7.5,
    display: "flex",
    position: "relative",
  },
  replyBannerSideColor: {
    flex: "none",
    width: 4,
    backgroundColor: "#35cd96",
  },
  replyBannerSideColorMine: {
    backgroundColor: "#6bcbef",
  },
  replyBannerBody: {
    padding: 10,
    minWidth: 0,
    display: "block",
    overflow: "hidden",
    color: theme.palette.text.primary,
  },
  replyBannerText: {
    fontSize: 13,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  },
  senderName: {
    display: "block",
    fontSize: 12.5,
    fontWeight: 500,
    padding: "3px 6px 0 6px",
  },
  textContentItem: {
    overflowWrap: "break-word",
    // A calha de 80px a direita reserva o espaco do horario absoluto.
    padding: "3px 80px 6px 6px",
    fontSize: 16,
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },
  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
    fontSize: 12,
  },
  mediaImage: {
    display: "block",
    maxWidth: 300,
    maxHeight: 300,
    width: "100%",
    borderRadius: 6,
    margin: "3px 0",
    cursor: "pointer",
  },
  mediaFile: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    margin: "4px 0",
    padding: "6px 8px",
    borderRadius: 6,
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    color: "#303030",
    textDecoration: "none",
    maxWidth: 320,
  },
  mediaFileName: {
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyMessages: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667781",
  },
  // Compositor com as mesmas cores do MessageInputCustom.
  composerWrapper: {
    backgroundColor: theme.palette.bordabox,
    display: "flex",
    flexDirection: "column",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    flex: "none",
  },
  composerBox: {
    backgroundColor: theme.palette.newmessagebox,
    width: "100%",
    display: "flex",
    padding: 7,
    alignItems: "center",
    position: "relative",
  },
  inputWrapper: {
    padding: 6,
    marginRight: 7,
    backgroundColor: theme.palette.inputdigita,
    display: "flex",
    borderRadius: 20,
    flex: 1,
  },
  input: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },
  composerIcons: {
    color: "grey",
  },
  emojiBox: {
    position: "absolute",
    bottom: 63,
    left: 0,
    zIndex: 10,
    borderTop: "1px solid #e8e8e8",
  },
  uploadInput: {
    display: "none",
  },
  previewList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "8px 12px",
    width: "100%",
    backgroundColor: theme.palette.newmessagebox,
  },
  previewCard: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid rgba(0, 0, 0, 0.15)",
    maxWidth: 220,
  },
  previewName: {
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const isImage = (fileName) => {
  const ext = String(fileName || "").split(".").pop().toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
};

const mediaUrl = (mediaPath) =>
  `${process.env.REACT_APP_BACKEND_URL}/public/internalChat/${mediaPath}`;

const getMessageDomId = (messageId) => `chat-message-${messageId}`;

const isImageMessage = (message) =>
  Boolean(message?.mediaPath) && isImage(message.mediaName || message.mediaPath);

const toViewerItem = (message) => ({
  id: String(message.id),
  type: "image",
  url: mediaUrl(message.mediaPath),
  fileName: message.mediaName || message.mediaPath,
  createdAt: message.createdAt,
  message,
});

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  handleEditChat,
  handleDeleteChat,
  scrollToBottomRef,
  pageInfo,
  loadingMore,
  replyingMessage,
  setReplyingMessage,
  jumpRequest,
  onJumpToMessage,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const baseRef = useRef();
  const listRef = useRef();
  const inputRef = useRef();
  // Telas de toque nao tem hover: a setinha de acoes fica sempre visivel nelas.
  const noHover = useMediaQuery("(hover: none)");

  const [contentMessage, setContentMessage] = useState("");
  // Visualizador de imagens: navega pelas imagens já carregadas desta conversa.
  const [viewerItem, setViewerItem] = useState(null);
  const viewerItems = useMemo(
    () => (viewerItem ? messages.filter(isImageMessage).map(toViewerItem) : undefined),
    [viewerItem, messages]
  );
  const [medias, setMedias] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Menu de acoes por mensagem. Sempre ancorado por posicao: tanto a setinha
  // quanto o botao direito abrem o menu num ponto da tela.
  const [menuMessage, setMenuMessage] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);

  // Salto para uma mensagem: id destacado no momento.
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  // Enquanto uma rolagem suave esta em curso, o handleScroll e ignorado: ele
  // atravessa a zona de gatilho do loadMore e dispararia carga em laco.
  const ignoreScrollUntilRef = useRef(0);
  // Posicao do scroll guardada antes de carregar historico mais antigo.
  const pendingRestoreRef = useRef(null);

  // Pesquisa dentro da conversa (painel lateral em ChatSearchDrawer).
  const [searchOpen, setSearchOpen] = useState(false);

  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    return el.scrollHeight - (el.scrollTop + el.clientHeight) < 150;
  };

  const scrollToBottom = (options) => {
    // Só arrasta para o fim quando pedido explicitamente (mensagem própria,
    // abertura da conversa) ou quando o usuário já estava no fim.
    if (!options?.force && !isNearBottom()) return;
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (target) => {
    if (!target?.users) return 0;
    const currentUser = target.users.find((u) => u.userId === user.id);
    return currentUser?.unreads || 0;
  };

  useEffect(() => {
    scrollToBottomRef.current = scrollToBottom;
  }, [scrollToBottomRef]);

  // A marcacao de lido roda a cada troca de conversa e sempre que chega mensagem
  // nova com a conversa aberta. Antes o effect tinha `[]` como dependencia e a
  // condicao comparava um booleano com `> 0` (unreadMessages ja devolvia
  // booleano), entao nunca disparava — a conversa nunca zerava o contador.
  const unreads = unreadMessages(chat);
  useEffect(() => {
    if (!chat?.id || unreads <= 0) return;
    api.post(`/chats/${chat.id}/read`, { userId: user.id }).catch(() => {});
  }, [chat?.id, unreads, user.id]);

  const participants = useMemo(() => {
    if (!chat?.users) return "";
    return chat.users
      .map((u) => u.user?.name)
      .filter(Boolean)
      .join(", ");
  }, [chat]);

  const handleScroll = (e) => {
    // Rolagem programada (salto) não pode disparar carga de histórico.
    if (Date.now() < ignoreScrollUntilRef.current) return;

    const { scrollTop, scrollHeight } = e.currentTarget;
    if (!pageInfo?.hasMore || loadingMore) return;
    if (scrollTop < 120) {
      // Guarda a métrica para reposicionar a viewport depois do prepend.
      // firstId identifica o prepend: se ele não mudar, a lista mudou por outro
      // motivo (mensagem nova no fim) e restaurar jogaria o usuário para o topo.
      pendingRestoreRef.current = {
        scrollHeight,
        scrollTop,
        firstId: messages[0]?.id,
      };
      handleLoadMore();
    }
  };

  // Ao carregar mensagens mais antigas elas entram ACIMA das atuais e empurram
  // tudo para baixo. Sem esta compensação a conversa "pula" a cada carga.
  useLayoutEffect(() => {
    const pending = pendingRestoreRef.current;
    const el = listRef.current;
    if (!pending || !el) return;
    if (messages[0]?.id === pending.firstId) return;

    pendingRestoreRef.current = null;
    el.scrollTop = el.scrollHeight - pending.scrollHeight + pending.scrollTop;
  }, [messages]);

  // Centraliza um elemento dentro da própria lista. scrollIntoView não serve:
  // ele rola também os contêineres ancestrais e desloca a página inteira.
  const scrollMessageIntoContainer = useCallback((element, behavior) => {
    const container = listRef.current;
    if (!container || !element) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const centerOffset = Math.max(
      0,
      container.clientHeight / 2 - element.clientHeight / 2
    );
    const target =
      container.scrollTop + (elementRect.top - containerRect.top) - centerOffset;

    container.scrollTo({ top: Math.max(0, target), behavior });
  }, []);

  const isMessageReasonablyVisible = useCallback((element) => {
    const container = listRef.current;
    if (!container || !element) return false;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const visible =
      Math.min(elementRect.bottom, containerRect.bottom) -
      Math.max(elementRect.top, containerRect.top);
    return visible >= Math.min(elementRect.height, containerRect.height) * 0.6;
  }, []);

  const centerTargetMessage = useCallback(
    (messageId) => {
      const targetId = String(messageId);
      const element = document.getElementById(getMessageDomId(targetId));
      if (!element) return false;

      ignoreScrollUntilRef.current = Date.now() + 1200;
      scrollMessageIntoContainer(element, "smooth");
      setHighlightedMessageId(targetId);

      window.setTimeout(() => {
        setHighlightedMessageId((prev) => (String(prev) === targetId ? null : prev));
      }, 2600);

      // Imagens carregando depois deslocam o layout: reafirma a posição.
      const reassert = () => {
        const el = document.getElementById(getMessageDomId(targetId));
        if (el && !isMessageReasonablyVisible(el)) {
          scrollMessageIntoContainer(el, "auto");
        }
      };
      requestAnimationFrame(reassert);
      [150, 400, 800].forEach((delay) => window.setTimeout(reassert, delay));

      return true;
    },
    [isMessageReasonablyVisible, scrollMessageIntoContainer]
  );

  // O pedido de salto chega do pai. O nó pode ainda não estar montado quando o
  // efeito roda (o setMessages e este efeito correm juntos), daí o retry.
  useEffect(() => {
    if (!jumpRequest?.id || !jumpRequest?.token) return;

    // Um salto pode ter trocado a lista inteira (carga ancorada). A métrica de
    // restauração guardada para um prepend não vale mais.
    pendingRestoreRef.current = null;

    if (centerTargetMessage(jumpRequest.id)) return;

    const retry = requestAnimationFrame(() => {
      centerTargetMessage(jumpRequest.id);
    });
    return () => cancelAnimationFrame(retry);
  }, [jumpRequest?.token, jumpRequest?.id, centerTargetMessage]);

  const handleAddEmoji = (emoji) => {
    setContentMessage((prev) => prev + emoji.native);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;
    setMedias((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = null;
  };

  const removeMedia = (index) => {
    setMedias((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = () => {
    const text = contentMessage.trim();
    if (!text && medias.length === 0) return;
    handleSendMessage(text, medias, replyingMessage?.id ?? null);
    setContentMessage("");
    setMedias([]);
    setShowEmoji(false);
    setReplyingMessage?.(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      // ESC desfaz uma coisa por vez: primeiro a resposta, depois a pesquisa.
      if (replyingMessage) {
        e.preventDefault();
        setReplyingMessage?.(null);
        return;
      }
      if (searchOpen) {
        e.preventDefault();
        closeSearch();
      }
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      // Sem o preventDefault a quebra de linha ficava no input depois do envio.
      e.preventDefault();
      submit();
    }
  };

  // Foca o compositor ao escolher uma mensagem para responder.
  useEffect(() => {
    if (replyingMessage) {
      inputRef.current?.focus();
    }
  }, [replyingMessage]);

  const closeMessageMenu = () => {
    setMenuPosition(null);
    setMenuMessage(null);
  };

  const handleOpenMessageMenu = (e, message) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom, left: rect.right });
    setMenuMessage(message);
  };

  const handleMessageContextMenu = (e, message) => {
    e.preventDefault();
    setMenuPosition({ top: e.clientY, left: e.clientX });
    setMenuMessage(message);
  };

  const handleReplyMessage = () => {
    if (menuMessage) setReplyingMessage?.(menuMessage);
    closeMessageMenu();
  };

  // Mensagem só com anexo não tem texto: copia o link do arquivo.
  const getCopyText = (message) => {
    const text = message?.message?.trim();
    if (text) return text;
    if (message?.mediaPath) return mediaUrl(message.mediaPath);
    return "";
  };

  const handleCopyMessage = async () => {
    const text = getCopyText(menuMessage);
    closeMessageMenu();

    if (!text) {
      toast.warn(i18n.t("internalChat.copy.empty"));
      return;
    }

    const copied = await copyTextToClipboard(text);
    if (copied) {
      toast.success(i18n.t("internalChat.copy.success"));
    } else {
      toast.error(i18n.t("internalChat.copy.error"));
    }
  };

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
  }, []);

  // Fecha a pesquisa e limpa o destaque ao trocar de conversa.
  useEffect(() => {
    setSearchOpen(false);
    setHighlightedMessageId(null);
    pendingRestoreRef.current = null;
  }, [chat?.id]);

  const safeFormat = (value, pattern) => {
    try {
      return format(parseISO(value), pattern);
    } catch (err) {
      return "";
    }
  };

  const renderMedia = (message) => {
    if (!message.mediaPath) return null;
    const url = mediaUrl(message.mediaPath);
    const name = message.mediaName || message.mediaPath;

    if (isImage(name)) {
      return (
        <img
          className={classes.mediaImage}
          src={url}
          alt={name}
          onClick={() => setViewerItem(toViewerItem(message))}
        />
      );
    }

    return (
      <a
        className={classes.mediaFile}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <InsertDriveFileIcon fontSize="small" />
        <span className={classes.mediaFileName}>{name}</span>
      </a>
    );
  };

  // Prévia curta usada no bloco citado e no banner de resposta.
  const quotedPreviewText = (message) => {
    const text = message?.message?.trim();
    if (text) return text;
    if (message?.mediaName) {
      return (
        <>
          <AttachFileIcon style={{ fontSize: "1em", verticalAlign: "-0.15em", marginRight: 2 }} />
          {message.mediaName}
        </>
      );
    }
    return "";
  };

  const quotedSenderLabel = (message) =>
    message?.senderId === user.id
      ? i18n.t("internalChat.reply.you")
      : message?.sender?.name || "";

  const renderQuotedMessage = (message, isMine) => {
    const quoted = message.quotedMsg;
    if (!quoted) return null;

    const quotedIsMine = quoted.senderId === user.id;
    const name = quotedSenderLabel(quoted);
    const showThumb = quoted.mediaPath && isImage(quoted.mediaName || quoted.mediaPath);

    const jump = (e) => {
      e.stopPropagation();
      onJumpToMessage?.(quoted.id);
    };

    return (
      <div
        className={clsx(classes.quotedContainer, {
          [classes.quotedContainerRight]: isMine,
        })}
        role="button"
        tabIndex={0}
        onClick={jump}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") jump(e);
        }}
      >
        <span
          className={clsx(classes.quotedSideColor, {
            [classes.quotedSideColorMine]: quotedIsMine,
          })}
        />
        <div className={classes.quotedBody}>
          {showThumb && (
            <img
              className={classes.quotedThumb}
              src={mediaUrl(quoted.mediaPath)}
              alt={quoted.mediaName || ""}
            />
          )}
          {!showThumb && quoted.mediaPath && (
            <InsertDriveFileIcon fontSize="small" />
          )}
          <div style={{ minWidth: 0 }}>
            <span
              className={classes.quotedSender}
              style={{ color: colorFromName(name) }}
            >
              {name}
            </span>
            <span className={classes.quotedText}>
              {quotedPreviewText(quoted)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (!Array.isArray(messages) || messages.length === 0) {
      return (
        <div className={classes.emptyMessages}>
          <Typography variant="body2">
            {i18n.t("internalChat.messages.empty")}
          </Typography>
        </div>
      );
    }

    return messages.map((item, index) => {
      const isMine = item.senderId === user.id;
      const previous = index > 0 ? messages[index - 1] : null;

      // Separador de dia sempre que a data muda em relacao a mensagem anterior.
      let dayDivider = null;
      let showDivider = !previous;
      if (previous) {
        try {
          showDivider = !isSameDay(
            parseISO(item.createdAt),
            parseISO(previous.createdAt)
          );
        } catch (err) {
          showDivider = false;
        }
      }
      if (showDivider) {
        dayDivider = (
          <span
            key={`divider-${item.id ?? index}`}
            className={classes.dailyTimestamp}
          >
            <div className={classes.dailyTimestampText}>
              {safeFormat(item.createdAt, "dd/MM/yyyy")}
            </div>
          </span>
        );
      }

      // O nome so aparece na primeira mensagem de uma sequencia do mesmo autor.
      const startsGroup =
        showDivider || !previous || previous.senderId !== item.senderId;
      const senderName = item.sender?.name;

      return (
        <React.Fragment key={item.id ?? index}>
          {dayDivider}
          <div
            id={getMessageDomId(item.id)}
            className={clsx(isMine ? classes.messageRight : classes.messageLeft, {
              [classes.messageGroupStart]: startsGroup && !showDivider,
              [classes.messageJumpHighlight]:
                String(highlightedMessageId) === String(item.id),
            })}
            onContextMenu={(e) => handleMessageContextMenu(e, item)}
          >
            <IconButton
              id="chatMessageActionsButton"
              size="small"
              className={clsx(classes.messageActionsButton, {
                [classes.messageActionsButtonAlwaysVisible]: noHover,
              })}
              onClick={(e) => handleOpenMessageMenu(e, item)}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
            {startsGroup && !isMine && (
              <span
                className={classes.senderName}
                style={{ color: colorFromName(senderName) }}
              >
                {senderName}
              </span>
            )}
            {renderQuotedMessage(item, isMine)}
            {renderMedia(item)}
            <div className={classes.textContentItem}>
              {item.message}
              <span className={classes.timestamp}>
                {safeFormat(item.createdAt, "HH:mm")}
              </span>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  const isOwner = chat?.ownerId === user.id;

  return (
    <Paper className={classes.mainContainer}>
      <ConfirmationModal
        title={i18n.t("internalChat.deleteConfirm.title")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDeleteChat(chat)}
      >
        {i18n.t("internalChat.deleteConfirm.message")}
      </ConfirmationModal>

      {/* Coluna da conversa. O painel de pesquisa é irmão dela e ocupa espaço
          de verdade no flex, então a conversa encolhe em vez de ficar coberta. */}
      <div className={classes.chatColumn}>
      <Card square className={classes.chatHeader}>
        <CardHeader
          titleTypographyProps={{ noWrap: true, variant: "subtitle1" }}
          subheaderTypographyProps={{ noWrap: true, variant: "caption" }}
          avatar={
            <Avatar
              className={classes.headerAvatar}
              style={{ backgroundColor: colorFromName(chat?.title) }}
            >
              {getInitials(chat?.title)}
            </Avatar>
          }
          title={chat?.title}
          subheader={participants}
        />
        {/* headerActions fora do isOwner: a lupa é para todo participante,
            só Editar/Excluir seguem restritos ao dono da conversa.
            Botões no mesmo padrão do cabeçalho de atendimento
            (TicketActionButtonsCustom): IconButton no tamanho padrão. */}
        <div className={classes.headerActions}>
          <Tooltip title={i18n.t("internalChat.search.open")}>
            <IconButton onClick={() => setSearchOpen((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          {isOwner && (
            <>
              <Tooltip title={i18n.t("internalChat.header.edit")}>
                <IconButton onClick={handleEditChat}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n.t("internalChat.header.delete")}>
                <IconButton onClick={() => setConfirmOpen(true)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </Card>


      <div
        ref={listRef}
        onScroll={handleScroll}
        className={classes.messageList}
      >
        {loadingMore && (
          <div className={classes.historyLoading}>
            <CircularProgress size={22} />
          </div>
        )}
        {renderMessages()}
        <div ref={baseRef}></div>
      </div>

      <Menu
        open={Boolean(menuMessage && menuPosition)}
        onClose={closeMessageMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition || undefined}
        getContentAnchorEl={null}
      >
        <MenuItem onClick={handleReplyMessage}>
          <ListItemIcon style={{ minWidth: 34 }}>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("internalChat.actions.reply")}
        </MenuItem>
        <MenuItem onClick={handleCopyMessage} disabled={!getCopyText(menuMessage)}>
          <ListItemIcon style={{ minWidth: 34 }}>
            <FileCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("internalChat.actions.copy")}
        </MenuItem>
      </Menu>

      <Paper square elevation={0} className={classes.composerWrapper}>
        {replyingMessage && (
          <div className={classes.replyBannerWrapper}>
            <div className={classes.replyBannerContainer}>
              <span
                className={clsx(classes.replyBannerSideColor, {
                  [classes.replyBannerSideColorMine]:
                    replyingMessage.senderId === user.id,
                })}
              />
              <div className={classes.replyBannerBody}>
                <span
                  className={classes.quotedSender}
                  style={{
                    color: colorFromName(quotedSenderLabel(replyingMessage)),
                  }}
                >
                  {quotedSenderLabel(replyingMessage)}
                </span>
                <span className={classes.replyBannerText}>
                  {quotedPreviewText(replyingMessage)}
                </span>
              </div>
            </div>
            <Tooltip title={i18n.t("internalChat.reply.cancel")}>
              <IconButton size="small" onClick={() => setReplyingMessage?.(null)}>
                <CloseIcon fontSize="small" className={classes.composerIcons} />
              </IconButton>
            </Tooltip>
          </div>
        )}
        {medias.length > 0 && (
          <div className={classes.previewList}>
            {medias.map((file, index) => (
              <div className={classes.previewCard} key={`${file.name}-${index}`}>
                <InsertDriveFileIcon fontSize="small" />
                <span className={classes.previewName}>{file.name}</span>
                <IconButton size="small" onClick={() => removeMedia(index)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <div className={classes.composerBox}>
          {showEmoji && (
            <div className={classes.emojiBox}>
              <Picker
                perLine={16}
                showPreview={false}
                showSkinTones={false}
                onSelect={handleAddEmoji}
              />
            </div>
          )}
          <Tooltip title="Emoji">
            <IconButton onClick={() => setShowEmoji((prev) => !prev)}>
              <MoodIcon className={classes.composerIcons} />
            </IconButton>
          </Tooltip>
          <input
            multiple
            type="file"
            id="internal-chat-upload"
            className={classes.uploadInput}
            onChange={handleChangeMedias}
          />
          <label htmlFor="internal-chat-upload">
            <Tooltip title={i18n.t("messagesInput.tooltips.attachFile")}>
              <IconButton aria-label="upload" component="span">
                <AttachFileIcon className={classes.composerIcons} />
              </IconButton>
            </Tooltip>
          </label>
          <div className={classes.inputWrapper}>
            <InputBase
              multiline
              maxRows={5}
              inputRef={inputRef}
              className={classes.input}
              placeholder={i18n.t("internalChat.messages.inputPlaceholder")}
              value={contentMessage}
              onKeyDown={handleKeyDown}
              onChange={(e) => setContentMessage(e.target.value)}
            />
          </div>
          <IconButton onClick={submit}>
            <SendIcon className={classes.composerIcons} />
          </IconButton>
        </div>
      </Paper>
      </div>

      <ChatSearchDrawer
        open={searchOpen}
        onClose={closeSearch}
        chatId={chat?.id}
        currentUserId={user.id}
        onJumpToMessage={onJumpToMessage}
      />

      {viewerItem && (
        <MediaGalleryViewer
          open
          onClose={() => setViewerItem(null)}
          initialItem={viewerItem}
          localItems={viewerItems}
        />
      )}
    </Paper>
  );
}
